import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listVendorDirectoryDuplicateMatches } from "../../api/clientVendorDirectoryDuplicateApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.duplicates";

const STATUS_FILTERS = ["pending", "deferred", "reviewed", "suppressed", ""];
const TYPE_FILTERS = ["", "candidate_vendor", "candidate_candidate", "vendor_vendor"];
const CONFIDENCE_FILTERS = ["", "exact", "very_likely", "likely", "possible", "conflict"];

function bandBadgeClass(band) {
  if (band === "exact" || band === "very_likely") return "cvd-badge--published";
  if (band === "conflict") return "cvd-badge--suspended";
  if (band === "likely") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function statusBadgeClass(status) {
  if (status === "reviewed") return "cvd-badge--published";
  if (status === "suppressed") return "cvd-badge--suspended";
  if (status === "deferred") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatScore(value) {
  if (value == null) return "—";
  return Number(value).toFixed(2);
}

function MetricCard({ label, value, hint }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? 0}</strong>
      {hint ? <span className="cvd-metric-card__hint">{hint}</span> : null}
    </article>
  );
}

function DuplicateStatusBadge({ status, t }) {
  return (
    <span className={`cvd-badge ${statusBadgeClass(status)}`}>
      {t(`${COPY_PREFIX}.status.${status}`, {}, status)}
    </span>
  );
}

function ConfidenceBadge({ band, t }) {
  return (
    <span className={`cvd-badge ${bandBadgeClass(band)}`}>
      {t(`${COPY_PREFIX}.confidence.${band}`, {}, band)}
    </span>
  );
}

export default function ClientVendorDirectoryDuplicateListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") ?? "pending";
  const matchType = searchParams.get("matchType") || "";
  const confidenceBand = searchParams.get("confidenceBand") || "";
  const page = Number(searchParams.get("page") || 1);
  const basePath = `${serviciosBase}/duplicados`;
  const viewName = activeView?.name || "CasaMapa";

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listVendorDirectoryDuplicateMatches(clientViewId, {
        page,
        pageSize: 25,
        status: status || undefined,
        matchType: matchType || undefined,
        confidenceBand: confidenceBand || undefined,
      });
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, confidenceBand, matchType, page, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const matches = data?.matches || [];
  const pagination = data?.pagination || { page: 1, totalPages: 0, total: 0 };

  const summary = useMemo(() => {
    const highConfidence = matches.filter((match) =>
      ["exact", "very_likely"].includes(match.confidenceBand)
    ).length;
    const conflicts = matches.filter((match) => match.confidenceBand === "conflict").length;
    return { total: pagination.total || 0, highConfidence, conflicts };
  }, [matches, pagination.total]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const setPage = (nextPage) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage > 1) next.set("page", String(nextPage));
    else next.delete("page");
    setSearchParams(next);
  };

  return (
    <div className="page-content cvd-admin cvd-duplicates">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
        </div>
        <div className="cvd-admin__actions">
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={load}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
        </div>
      </div>

      {!loading && matches.length > 0 ? (
        <div className="cvd-metric-grid">
          <MetricCard label={t(`${COPY_PREFIX}.kpi.total`)} value={summary.total} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.highConfidence`)} value={summary.highConfidence} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.conflicts`)} value={summary.conflicts} />
        </div>
      ) : null}

      <div className="cvd-duplicate-filters">
        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterStatus`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterStatus`)}>
            {STATUS_FILTERS.map((value) => (
              <button
                key={value || "all-status"}
                type="button"
                className={`cvd-run-filter-chip${status === value ? " is-active" : ""}`}
                onClick={() => updateParam("status", value)}
              >
                {value ? t(`${COPY_PREFIX}.status.${value}`) : t(`${COPY_PREFIX}.allStatuses`)}
              </button>
            ))}
          </div>
        </div>

        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterType`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterType`)}>
            {TYPE_FILTERS.map((value) => (
              <button
                key={value || "all-type"}
                type="button"
                className={`cvd-run-filter-chip${matchType === value ? " is-active" : ""}`}
                onClick={() => updateParam("matchType", value)}
              >
                {value ? t(`${COPY_PREFIX}.matchType.${value}`) : t(`${COPY_PREFIX}.allTypes`)}
              </button>
            ))}
          </div>
        </div>

        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterConfidence`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterConfidence`)}>
            {CONFIDENCE_FILTERS.map((value) => (
              <button
                key={value || "all-confidence"}
                type="button"
                className={`cvd-run-filter-chip${confidenceBand === value ? " is-active" : ""}`}
                onClick={() => updateParam("confidenceBand", value)}
              >
                {value ? t(`${COPY_PREFIX}.confidence.${value}`) : t(`${COPY_PREFIX}.allConfidence`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage && matches.length === 0 ? (
        <section className="cvd-run-empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
        </section>
      ) : null}

      {!loading && !errorMessage && matches.length > 0 ? (
        <div className="cvd-run-list">
          {matches.map((match) => (
            <article key={match.id} className="cvd-run-card cvd-duplicate-card">
              <div className="cvd-run-card__main">
                <div className="cvd-run-card__head">
                  <div className="cvd-duplicate-card__records">
                    <div className="cvd-duplicate-card__record">
                      <span className="cvd-duplicate-card__record-label">{t(`${COPY_PREFIX}.table.recordA`)}</span>
                      <strong>{match.recordAName || "—"}</strong>
                    </div>
                    <span className="cvd-duplicate-card__versus" aria-hidden="true">
                      ↔
                    </span>
                    <div className="cvd-duplicate-card__record">
                      <span className="cvd-duplicate-card__record-label">{t(`${COPY_PREFIX}.table.recordB`)}</span>
                      <strong>{match.recordBName || "—"}</strong>
                    </div>
                  </div>
                  <div className="cvd-duplicate-card__badges">
                    <DuplicateStatusBadge status={match.status} t={t} />
                    <ConfidenceBadge band={match.confidenceBand || "possible"} t={t} />
                  </div>
                </div>

                <div className="cvd-run-card__stats">
                  <span>
                    {t(`${COPY_PREFIX}.table.type`)}:{" "}
                    <strong>{t(`${COPY_PREFIX}.matchType.${match.matchType}`, {}, match.matchType)}</strong>
                  </span>
                  <span>
                    {t(`${COPY_PREFIX}.table.score`)}: <strong>{formatScore(match.matchScore)}</strong>
                  </span>
                  <span className="cvd-duplicate-card__signals">
                    {(match.strongestSignals || []).slice(0, 3).join(" · ") || "—"}
                  </span>
                </div>
              </div>

              <div className="cvd-run-card__actions">
                <Link className="cvd-btn cvd-btn--primary" to={`${basePath}/${match.id}`}>
                  {t(`${COPY_PREFIX}.viewDetails`)}
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}

      {!loading && !errorMessage && pagination.totalPages > 1 ? (
        <nav className="cvd-duplicate-pagination" aria-label={t(`${COPY_PREFIX}.page`, { page, totalPages: pagination.totalPages })}>
          <button
            type="button"
            className="cvd-btn cvd-btn--ghost"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {t(`${COPY_PREFIX}.prevPage`)}
          </button>
          <span className="muted">
            {t(`${COPY_PREFIX}.page`, { page, totalPages: pagination.totalPages })}
          </span>
          <button
            type="button"
            className="cvd-btn cvd-btn--ghost"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(page + 1)}
          >
            {t(`${COPY_PREFIX}.nextPage`)}
          </button>
        </nav>
      ) : null}
    </div>
  );
}
